import { useState, useRef, useEffect } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Input, Button, AgentDot } from './index';
import clsx from 'clsx';

type Msg = { role: 'user' | 'assistant'; content: string; ts: number; mode?: 'quick' | 'full' };
type Mode = 'quick' | 'full';

const QUICK_SUGGESTIONS = [
  'what deals are blocked right now?',
  'who in the group focuses on lng?',
  'summarize what changed this week',
  'find me a dinner spot in geneva for thursday',
];

const FULL_SUGGESTIONS = [
  'draft a 2-page memo on the lagos terminal deal',
  'build a chart of our deals by sector',
  'research current sanctions exposure for pemex',
  'review my term sheet against our standard playbook',
];

export function AgentChat() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('quick');
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{
        role: 'assistant',
        ts: Date.now(),
        content: "hi — i'm the inner circle agent. quick mode answers in seconds with claude (full inner circle context). full obvious mode opens a real obvious thread with all your skills (writing, dataviz, observability, the lot). try a suggestion or ask anything.",
      }]);
    }
  }, [open, msgs.length]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOpen(o => { if (!o) setTimeout(() => inputRef.current?.focus(), 50); return !o; });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    const userMsg: Msg = { role: 'user', content: trimmed, ts: Date.now(), mode };
    const next = [...msgs, userMsg];
    setMsgs(next);
    setInput('');
    setBusy(true);

    try {
      if (mode === 'quick') {
        const history = next.slice(-10).map(m => ({ role: m.role, content: m.content }));
        const r = await api.agentChat(trimmed, history);
        setMsgs(prev => [...prev, { role: 'assistant', content: r.reply, ts: Date.now(), mode: 'quick' }]);
      } else {
        // Full Obvious — generate prompt + open obvious.ai
        const history = next.filter(m => m.mode === 'quick').slice(-6).map(m => ({ role: m.role, content: m.content }));
        const r = await api.obviousHandoff(trimmed, undefined, undefined, history);
        // Copy prompt to clipboard
        try { await navigator.clipboard.writeText(r.prompt); } catch {}
        // Open obvious in new tab
        window.open(r.obviousUrl, '_blank', 'noopener');

        setMsgs(prev => [...prev, {
          role: 'assistant',
          content: `opened obvious in a new tab. the prompt is copied to your clipboard — paste it into a new thread there. when obvious finishes, the artifact comes back to your inner circle vault automatically.`,
          ts: Date.now(),
          mode: 'full',
        }, {
          role: 'assistant',
          ts: Date.now() + 1,
          content: '```\n' + r.prompt.slice(0, 800) + (r.prompt.length > 800 ? '\n…' : '') + '\n```',
          mode: 'full',
        }]);
      }
    } catch (e: any) {
      setMsgs(prev => [...prev, { role: 'assistant', content: `couldn't reach the agent — ${e.message}`, ts: Date.now() }]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }

  const suggestions = mode === 'quick' ? QUICK_SUGGESTIONS : FULL_SUGGESTIONS;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 hairline bg-ink text-bg rounded-sm px-3 h-10 flex items-center gap-2 hover:bg-[#1f1f1f] transition-colors"
          title="ask the agent (⌘/)"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent pulse-dot" />
          <span className="text-sm tracking-tight">ask the agent</span>
          <span className="hairline rounded-sm px-1 text-[10px] font-mono opacity-70">⌘/</span>
        </button>
      )}

      <div className={clsx(
        'fixed bottom-0 right-0 z-40 w-[460px] max-w-[100vw] bg-bg hairline rounded-tl-sm flex flex-col transition-all duration-200 ease-out',
        open ? 'translate-y-0 h-[600px] max-h-[85vh]' : 'translate-y-full h-0',
      )}>
        <div className="hairline-b px-3 h-10 flex items-center justify-between shrink-0">
          <Row gap={1} align="center">
            <AgentDot active />
            <Text variant="micro">inner circle agent</Text>
          </Row>
          <Row gap={0}>
            <button onClick={() => setMsgs([])} className="text-xs text-muted hover:text-ink px-2">new chat</button>
            <button onClick={() => setOpen(false)} className="text-xs text-muted hover:text-ink px-2">close</button>
          </Row>
        </div>

        {/* Mode toggle */}
        <div className="hairline-b px-2 py-2 shrink-0 flex">
          <button
            onClick={() => setMode('quick')}
            className={clsx(
              'flex-1 px-2 py-1.5 text-xs rounded-sm transition-colors hairline',
              mode === 'quick' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink',
            )}>
            <div className="font-medium">quick chat</div>
            <div className="text-[10px] opacity-70">claude · here in app</div>
          </button>
          <div className="w-1" />
          <button
            onClick={() => setMode('full')}
            className={clsx(
              'flex-1 px-2 py-1.5 text-xs rounded-sm transition-colors hairline',
              mode === 'full' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink',
            )}>
            <div className="font-medium">full obvious</div>
            <div className="text-[10px] opacity-70">all skills · opens obvious</div>
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3">
          <Stack gap={2}>
            {msgs.map((m, i) => (
              <div key={i} className={clsx('max-w-[88%]', m.role === 'user' ? 'ml-auto' : '')}>
                <div className={clsx(
                  'rounded-sm px-2 py-1.5 text-sm tracking-tight whitespace-pre-wrap',
                  m.role === 'user' ? 'bg-ink text-bg' : 'hairline bg-whisper',
                  m.mode === 'full' && m.role === 'assistant' && 'border-accent',
                )}>
                  {m.content}
                </div>
                {m.mode === 'full' && m.role === 'user' && (
                  <Text variant="label" className="text-right mt-0.5">handed off to obvious</Text>
                )}
              </div>
            ))}
            {busy && (
              <div className="hairline bg-whisper rounded-sm px-2 py-1.5 max-w-[88%]">
                <Row gap={1} align="center">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent pulse-dot" />
                  <Text variant="label">{mode === 'full' ? 'preparing obvious handoff…' : 'thinking…'}</Text>
                </Row>
              </div>
            )}
            {msgs.length <= 1 && !busy && (
              <Stack gap={1} className="mt-1">
                <Text variant="label">{mode === 'quick' ? 'quick suggestions' : 'full obvious works best for'}</Text>
                {suggestions.map(s => (
                  <button key={s} onClick={() => send(s)} className="text-left hairline rounded-sm px-2 py-1 text-xs text-muted hover:text-ink hover:bg-whisper transition-colors">
                    {s}
                  </button>
                ))}
              </Stack>
            )}
          </Stack>
        </div>

        <div className="hairline-t p-2 shrink-0">
          <Row gap={1}>
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send(input))}
              placeholder={mode === 'quick' ? 'ask anything…' : 'what should obvious do?'}
              className="flex-1"
            />
            <Button variant="primary" size="sm" onClick={() => send(input)} disabled={busy || !input.trim()}>
              {mode === 'full' ? 'send to obvious' : 'send'}
            </Button>
          </Row>
          {mode === 'full' && (
            <Text variant="label" className="mt-1">opens a thread on obvious.ai with full deal context. result lands back in your vault.</Text>
          )}
        </div>
      </div>
    </>
  );
}
